import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { API } from "../App";
import { Users, Plus, Search, Filter, CheckCircle, Clock, Calendar, Building2, Mail, Phone, User, Trash2, Loader2, PlayCircle, PauseCircle, RefreshCw, FileText, Link2, Settings2, ArrowLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Dialog } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { StatCard, CustomerCard, StepItem, progressTextClass, progressBarClass } from "../components/onboarding/OnboardingCards";
import { AddStepDialog, EditStepDialog, DeleteCustomerDialog } from "../components/onboarding/StepDialogs";
import { AddCustomerDialog, TemplateDialog } from "../components/onboarding/CustomerDialogs";

const CustomerOnboardingPage = () => {
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Selected customer for detail view
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Add customer dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    email: "",
    company: "",
    contact_person: "",
    phone: "",
    start_date: "",
    template_id: undefined
  });
  const [saving, setSaving] = useState(false);
  
  // Add step dialog
  const [addStepDialogOpen, setAddStepDialogOpen] = useState(false);
  const [newStep, setNewStep] = useState({
    title: "",
    description: "",
    due_date: ""
  });
  
  // Edit step dialog
  const [editStepDialogOpen, setEditStepDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingCustomer, setDeletingCustomer] = useState(null);
  
  // Template management
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    description: "",
    steps: [{ title: "", description: "" }],
    is_default: false
  });

  const fetchData = useCallback(async () => {
    try {
      const [customersRes, templatesRes, statsRes] = await Promise.all([
        axios.get(`${API}/onboarding/customers`, {
          params: { status: statusFilter !== "all" ? statusFilter : undefined },
          withCredentials: true
        }),
        axios.get(`${API}/onboarding/templates`, { withCredentials: true }),
        axios.get(`${API}/onboarding/stats`, { withCredentials: true })
      ]);
      
      setCustomers(customersRes.data.customers || []);
      setTemplates(templatesRes.data.templates || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load onboarding data");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchCustomerDetail = async (customerId) => {
    try {
      const res = await axios.get(`${API}/onboarding/customers/${customerId}`, {
        withCredentials: true
      });
      setSelectedCustomer(res.data);
    } catch (error) {
      toast.error("Failed to load customer details");
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/onboarding/customers`, {
        ...newCustomer,
        template_id: newCustomer.template_id || undefined
      }, { withCredentials: true });
      
      toast.success("Customer added!");
      setAddDialogOpen(false);
      setNewCustomer({
        name: "",
        email: "",
        company: "",
        contact_person: "",
        phone: "",
        start_date: "",
        template_id: undefined
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to add customer");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (customerId, newStatus) => {
    try {
      await axios.put(`${API}/onboarding/customers/${customerId}`, {
        status: newStatus
      }, { withCredentials: true });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
      if (selectedCustomer?.customer_id === customerId) {
        fetchCustomerDetail(customerId);
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteCustomer = async () => {
    try {
      await axios.delete(`${API}/onboarding/customers/${deletingCustomer.customer_id}`, {
        withCredentials: true
      });
      toast.success("Customer deleted");
      setDeleteDialogOpen(false);
      setDeletingCustomer(null);
      if (selectedCustomer?.customer_id === deletingCustomer.customer_id) {
        setSelectedCustomer(null);
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to delete customer");
    }
  };

  const handleToggleStep = async (stepId, isCompleted) => {
    try {
      await axios.put(
        `${API}/onboarding/customers/${selectedCustomer.customer_id}/steps/${stepId}`,
        { is_completed: !isCompleted },
        { withCredentials: true }
      );
      fetchCustomerDetail(selectedCustomer.customer_id);
      fetchData();
    } catch (error) {
      toast.error("Failed to update step");
    }
  };

  const handleAddStep = async () => {
    if (!newStep.title) {
      toast.error("Step title is required");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/onboarding/customers/${selectedCustomer.customer_id}/steps`,
        newStep,
        { withCredentials: true }
      );
      toast.success("Step added!");
      setAddStepDialogOpen(false);
      setNewStep({ title: "", description: "", due_date: "" });
      fetchCustomerDetail(selectedCustomer.customer_id);
      fetchData();
    } catch (error) {
      toast.error("Failed to add step");
    } finally {
      setSaving(false);
    }
  };

  const handleEditStep = async () => {
    if (!editingStep.title) {
      toast.error("Step title is required");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/onboarding/customers/${selectedCustomer.customer_id}/steps/${editingStep.step_id}`,
        {
          title: editingStep.title,
          description: editingStep.description,
          due_date: editingStep.due_date || null
        },
        { withCredentials: true }
      );
      toast.success("Step updated!");
      setEditStepDialogOpen(false);
      setEditingStep(null);
      fetchCustomerDetail(selectedCustomer.customer_id);
    } catch (error) {
      toast.error("Failed to update step");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = async (stepId) => {
    try {
      await axios.delete(
        `${API}/onboarding/customers/${selectedCustomer.customer_id}/steps/${stepId}`,
        { withCredentials: true }
      );
      toast.success("Step deleted");
      fetchCustomerDetail(selectedCustomer.customer_id);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete step");
    }
  };

  const handleUpdateStepNotes = async (stepId, notes) => {
    try {
      await axios.put(
        `${API}/onboarding/customers/${selectedCustomer.customer_id}/steps/${stepId}`,
        { notes },
        { withCredentials: true }
      );
      toast.success("Notes saved!");
      fetchCustomerDetail(selectedCustomer.customer_id);
    } catch (error) {
      toast.error("Failed to save notes");
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name) {
      toast.error("Template name is required");
      return;
    }
    
    const validSteps = templateForm.steps.filter(s => s.title.trim());
    if (validSteps.length === 0) {
      toast.error("Add at least one step");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/onboarding/templates`, {
        name: templateForm.name,
        description: templateForm.description,
        steps: validSteps.map((s, i) => ({ ...s, order: i })),
        is_default: templateForm.is_default
      }, { withCredentials: true });
      
      toast.success("Template saved!");
      setTemplateDialogOpen(false);
      setTemplateForm({
        name: "",
        description: "",
        steps: [{ title: "", description: "" }],
        is_default: false
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  // Filter customers by search
  const filteredCustomers = customers.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      c.name?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.company?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <Layout title="Customer Onboarding">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  // Detail view
  if (selectedCustomer) {
    const progress = selectedCustomer.progress || { total: 0, completed: 0, percentage: 0 };
    const sortedSteps = [...(selectedCustomer.steps || [])].sort((a, b) => a.order - b.order);
    
    return (
      <Layout title="Customer Onboarding">
        <div className="space-y-6">
          {/* Back button and header */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => setSelectedCustomer(null)}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to List
            </Button>
          </div>
          
          {/* Customer Info Card */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl font-bold text-slate-900">{selectedCustomer.name}</h1>
                    {selectedCustomer.status === "completed" && (
                      <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>
                    )}
                    {selectedCustomer.status === "paused" && (
                      <Badge className="bg-amber-100 text-amber-700">Paused</Badge>
                    )}
                    {selectedCustomer.status === "active" && (
                      <Badge className="bg-blue-100 text-blue-700">Active</Badge>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                    {selectedCustomer.company && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {selectedCustomer.company}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {selectedCustomer.email}
                    </span>
                    {selectedCustomer.contact_person && (
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {selectedCustomer.contact_person}
                      </span>
                    )}
                    {selectedCustomer.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {selectedCustomer.phone}
                      </span>
                    )}
                    {selectedCustomer.start_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Started: {new Date(selectedCustomer.start_date).toLocaleDateString()}
                      </span>
                    )}
                    {selectedCustomer.hubspot_contact_id && (
                      <Badge variant="outline" className="text-xs">
                        <Link2 className="w-3 h-3 mr-1" />
                        HubSpot Linked
                      </Badge>
                    )}
                  </div>
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <Settings2 className="w-4 h-4" />
                      Actions
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {selectedCustomer.status === "active" && (
                      <DropdownMenuItem onClick={() => handleStatusChange(selectedCustomer.customer_id, "paused")}>
                        <PauseCircle className="w-4 h-4 mr-2" />
                        Pause Onboarding
                      </DropdownMenuItem>
                    )}
                    {selectedCustomer.status === "paused" && (
                      <DropdownMenuItem onClick={() => handleStatusChange(selectedCustomer.customer_id, "active")}>
                        <PlayCircle className="w-4 h-4 mr-2" />
                        Resume Onboarding
                      </DropdownMenuItem>
                    )}
                    {selectedCustomer.status !== "completed" && (
                      <DropdownMenuItem onClick={() => handleStatusChange(selectedCustomer.customer_id, "completed")}>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark Complete
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => { setDeletingCustomer(selectedCustomer); setDeleteDialogOpen(true); }}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Customer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {/* Progress */}
              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-700">Onboarding Progress</span>
                  <span className={`text-lg font-bold ${progressTextClass(progress.percentage)}`}>
                    {progress.percentage}%
                  </span>
                </div>
                <Progress 
                  value={progress.percentage} 
                  className={`h-3 ${progressBarClass(progress.percentage)}`}
                />
                <p className="text-sm text-slate-500 mt-2">
                  {progress.completed} of {progress.total} steps completed
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* Steps */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Onboarding Steps</CardTitle>
                  <CardDescription>Track progress through each onboarding step</CardDescription>
                </div>
                <Button
                  onClick={() => setAddStepDialogOpen(true)}
                  className="gap-2 bg-[#425B76] hover:bg-[#37495f]"
                >
                  <Plus className="w-4 h-4" />
                  Add Step
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {sortedSteps.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No steps yet</p>
                  <p className="text-sm text-slate-400">Add steps to track onboarding progress</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sortedSteps.map((step) => (
                    <StepItem
                      key={step.step_id}
                      step={step}
                      onToggle={() => handleToggleStep(step.step_id, step.is_completed)}
                      onEdit={() => {
                        setEditingStep({
                          ...step,
                          due_date: step.due_date ? new Date(step.due_date).toISOString().split('T')[0] : ""
                        });
                        setEditStepDialogOpen(true);
                      }}
                      onDelete={() => handleDeleteStep(step.step_id)}
                      onNotesUpdate={(notes) => handleUpdateStepNotes(step.step_id, notes)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Add Step Dialog */}
        <AddStepDialog open={addStepDialogOpen} onOpenChange={setAddStepDialogOpen} newStep={newStep} setNewStep={setNewStep} onSave={handleAddStep} saving={saving} />
        <EditStepDialog open={editStepDialogOpen} onOpenChange={setEditStepDialogOpen} editingStep={editingStep} setEditingStep={setEditingStep} onSave={handleEditStep} saving={saving} />
        <DeleteCustomerDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} customer={deletingCustomer} onConfirm={handleDeleteCustomer} />
      </Layout>
    );
  }

  // List view
  return (
    <Layout title="Customer Onboarding">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#425B76] to-[#37495f] flex items-center justify-center shadow-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Customer Onboarding</h1>
              <p className="text-sm text-slate-500">Track and manage customer onboarding progress</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setTemplateDialogOpen(true)}
              className="gap-2"
            >
              <FileText className="w-4 h-4" />
              Templates
            </Button>
            <Button
              onClick={() => setAddDialogOpen(true)}
              className="gap-2 bg-[#425B76] hover:bg-[#37495f]"
              data-testid="add-customer-btn"
            >
              <Plus className="w-4 h-4" />
              Add Customer
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <StatCard
              icon={Users}
              label="Total Customers"
              value={stats.total_customers}
              color="bg-slate-600"
            />
            <StatCard
              icon={PlayCircle}
              label="Active"
              value={stats.active}
              color="bg-blue-500"
            />
            <StatCard
              icon={CheckCircle}
              label="Completed"
              value={stats.completed}
              color="bg-emerald-500"
            />
            <StatCard
              icon={PauseCircle}
              label="Paused"
              value={stats.paused}
              color="bg-amber-500"
            />
            <StatCard
              icon={Clock}
              label="Avg Progress"
              value={`${stats.average_progress}%`}
              color="bg-violet-500"
              subtext={stats.overdue_steps > 0 ? `${stats.overdue_steps} overdue` : undefined}
            />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Customer List */}
        {filteredCustomers.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No customers yet</p>
              <p className="text-sm text-slate-400 mt-1">Add your first customer to start tracking onboarding</p>
              <Button
                onClick={() => setAddDialogOpen(true)}
                className="mt-4 gap-2 bg-[#425B76] hover:bg-[#37495f]"
              >
                <Plus className="w-4 h-4" />
                Add Customer
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCustomers.map((customer) => (
              <CustomerCard
                key={customer.customer_id}
                customer={customer}
                onClick={() => fetchCustomerDetail(customer.customer_id)}
                onStatusChange={(status) => handleStatusChange(customer.customer_id, status)}
                onDelete={() => { setDeletingCustomer(customer); setDeleteDialogOpen(true); }}
              />
            ))}
          </div>
        )}

        {/* Add Customer Dialog */}
        <AddCustomerDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} newCustomer={newCustomer} setNewCustomer={setNewCustomer} templates={templates} onSave={handleAddCustomer} saving={saving} />
        <TemplateDialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen} templates={templates} templateForm={templateForm} setTemplateForm={setTemplateForm} onSave={handleSaveTemplate} saving={saving} />
        <DeleteCustomerDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} customer={deletingCustomer} onConfirm={handleDeleteCustomer} />
      </div>
    </Layout>
  );
};

export default CustomerOnboardingPage;
